import { createBrowserRouter } from 'react-router-dom';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Public Pages
import Home from './pages/Home';
import Brand from './pages/Brand';
import Branches from './pages/Branches';
import BranchDetail from './pages/BranchDetail';
import Menu from './pages/Menu';
import MenuBranch from './pages/MenuBranch';
import Catering from './pages/Catering';
import NasiDus from './pages/NasiDus';
import Careers from './pages/Careers';
import Apply from './pages/Apply';
import News from './pages/News';
import NewsDetail from './pages/NewsDetail';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DeleteAccount from './pages/DeleteAccount';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import Account from './pages/Account';
import Download from './pages/Download';

// Standalone Pages (no layout)
import Questionnaire from './pages/Questionnaire';
import PublicForm from './pages/PublicForm';

// Lazy load portal pages (code splitting)
import { lazy, Suspense } from 'react';

const AdminPanel = lazy(() => import('./pages/admin'));
const InvestorPortal = lazy(() => import('./pages/investor'));
const CustomerPortal = lazy(() => import('./pages/customer'));

// Loading fallback for lazy loaded pages
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

export const router = createBrowserRouter([
  // Public routes with main layout
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: <Home /> },
      { path: 'brand', element: <Brand /> },
      { path: 'branches', element: <Branches /> },
      { path: 'branches/:branchId', element: <BranchDetail /> },
      { path: 'menu', element: <Menu /> },
      { path: 'menu/:branchId', element: <MenuBranch /> },
      { path: 'catering', element: <Catering /> },
      { path: 'nasi-dus', element: <NasiDus /> },
      { path: 'careers', element: <Careers /> },
      { path: 'careers/apply/:positionId', element: <Apply /> },
      { path: 'news', element: <News /> },
      { path: 'news/:slug', element: <NewsDetail /> },
      { path: 'contact', element: <Contact /> },
      { path: 'privacy-policy', element: <PrivacyPolicy /> },
      { path: 'delete-account', element: <DeleteAccount /> },
    ],
  },

  // Auth routes (no layout wrapper)
  { path: '/login', element: <Login /> },
  { path: '/register', element: <Register /> },
  { path: '/forgot-password', element: <ForgotPassword /> },

  // Account page - any authenticated user (with layout)
  {
    path: '/account',
    element: (
      <ProtectedRoute>
        <Account />
      </ProtectedRoute>
    ),
  },

  // Download Portal - Super Admin only
  {
    path: '/download',
    element: (
      <ProtectedRoute allowedRoles={['super-admin']}>
        <Download />
      </ProtectedRoute>
    ),
  },

  // Media/Admin Panel - Protected
  {
    path: '/admin/*',
    element: (
      <ProtectedRoute allowedRoles={['media', 'marketing', 'admin', 'super-admin']}>
        <Suspense fallback={<PageLoader />}>
          <AdminPanel />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Investor Portal - Protected
  {
    path: '/investor/*',
    element: (
      <ProtectedRoute allowedRoles={['investor', 'admin', 'super-admin']}>
        <Suspense fallback={<PageLoader />}>
          <InvestorPortal />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Customer Portal - Protected
  {
    path: '/portal/*',
    element: (
      <ProtectedRoute allowedRoles={['customer', 'admin', 'super-admin']}>
        <Suspense fallback={<PageLoader />}>
          <CustomerPortal />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Standalone routes (no layout, for embedding/external use)
  { path: '/questionnaire/:sessionId', element: <Questionnaire /> },
  // Both paths resolve through one door: a printed code keeps working even
  // if it was printed under the other path's URL.
  { path: '/feedback/:code', element: <PublicForm /> },
  { path: '/survey/:code', element: <PublicForm /> },
]);
